import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    budget: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.form.success);
      setStatus("success");
      setForm({ name: "", email: "", company: "", service: "", budget: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form.error);
      setStatus("error");
    }
  };

  return (
    <section
      id="contact"
      className="relative py-32 md:py-48 px-6 md:px-12 bg-[#050505] border-t hairline"
      data-testid="contact-section"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-8">
            <div className="flex items-center gap-4 mb-8">
              <span className="inline-block w-8 h-px bg-[color:var(--accent)]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[color:var(--muted)]">
                {t.contact.label}
              </span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-[22vw] md:text-[15vw] lg:text-[13vw] font-extrabold leading-[0.85] tracking-[-0.04em] whitespace-pre-line"
            >
              {t.contact.title}
              <span className="text-[color:var(--accent)]">.</span>
            </motion.h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-base md:text-lg text-[color:var(--muted)] leading-relaxed">
              {t.contact.subtitle}
            </p>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-2"
          data-testid="contact-form"
        >
          <div className="md:col-span-6">
            <input
              className="raw-input"
              placeholder={t.contact.form.name}
              value={form.name}
              onChange={update("name")}
              required
              data-testid="contact-input-name"
            />
          </div>
          <div className="md:col-span-6">
            <input
              className="raw-input"
              type="email"
              placeholder={t.contact.form.email}
              value={form.email}
              onChange={update("email")}
              required
              data-testid="contact-input-email"
            />
          </div>
          <div className="md:col-span-6">
            <input
              className="raw-input"
              placeholder={t.contact.form.company}
              value={form.company}
              onChange={update("company")}
              data-testid="contact-input-company"
            />
          </div>
          <div className="md:col-span-3">
            <select
              className="raw-input"
              value={form.service}
              onChange={update("service")}
              data-testid="contact-input-service"
            >
              <option value="" className="bg-[#050505]">
                {t.contact.form.service}
              </option>
              {t.services.items.map((s) => (
                <option key={s.id} value={s.name} className="bg-[#050505]">
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <select
              className="raw-input"
              value={form.budget}
              onChange={update("budget")}
              data-testid="contact-input-budget"
            >
              <option value="" className="bg-[#050505]">
                {t.contact.form.budget}
              </option>
              {t.contact.budgetOptions.map((b) => (
                <option key={b} value={b} className="bg-[#050505]">
                  {b}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-12 mt-8">
            <textarea
              rows={4}
              className="raw-input resize-none"
              placeholder={t.contact.form.message}
              value={form.message}
              onChange={update("message")}
              required
              data-testid="contact-input-message"
            />
          </div>

          <div className="md:col-span-12 mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--muted)]">
              hello@hkbrandlab.com  ·  Hong Kong · Dubai · Shanghai · Milano
            </div>
            <button
              type="submit"
              disabled={status === "sending"}
              className="magnetic disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {status === "sending" ? t.contact.form.sending : t.contact.form.submit}
              <span aria-hidden className="ml-3">→</span>
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
